'use client';

/**
 * ChatSectionRenderer - Dispatches to section component based on CHAT_SCHEMA config
 *
 * Maps ChatSectionConfig.type to the corresponding React component.
 * Follows same pattern as details/section-renderer.tsx.
 *
 * @layer presentation
 * @arch_component_type renderer
 * @arch_domain copilot
 */

import type { ChatSectionConfig, MessageItem, ContactSummary, MessengerChannel, WhatsAppMediaAttachment } from '@/lib/api/types';
import { MessagesSection, WhatsAppSendSection, ChatterPostSection, MessengerTabsSection } from './sections';

interface ChatSectionRendererProps {
  config: ChatSectionConfig;
  data: ChatPanelData;
}

export interface ChatPanelData {
  messages: MessageItem[];
  messagesLoading?: boolean;
  onRefreshMessages?: () => void;
  messagesHeader?: string;
  messagesMetadata?: {
    message_count?: number;
    rfq_count?: number;
    po_count?: number;
  } | null;
  contacts: ContactSummary[];
  contactsLoading?: boolean;
  // Messaging callbacks
  onSendWhatsApp: (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => Promise<{ success: boolean; error?: string }>;
  onSendWeChat?: (wechatId: string, message: string) => Promise<{ success: boolean; error?: string }>;
  onPostToChatter?: (message: string) => Promise<{ success: boolean; message_id?: number; error?: string }>;
  // Loading states
  isSending?: boolean;  // Legacy: WhatsApp only
  isSendingWhatsApp?: boolean;
  isSendingWeChat?: boolean;
  isPostingChatter?: boolean;
}

/**
 * Simple condition evaluator.
 * Supports basic expressions like "data.customer_phone"
 */
function evaluateCondition(condition: string, data: ChatPanelData): boolean {
  try {
    const fn = new Function('data', `return Boolean(${condition})`);
    return fn(data);
  } catch (err) {
    console.warn(`[ChatSectionRenderer] Failed to evaluate condition: ${condition}`, err);
    return true; // Default to showing section on error
  }
}

export function ChatSectionRenderer({ config, data }: ChatSectionRendererProps) {
  // Conditional rendering - check condition against data
  if (config.condition && !evaluateCondition(config.condition, data)) {
    return null;
  }

  switch (config.type) {
    case 'messages':
      return (
        <MessagesSection
          messages={data.messages}
          isLoading={data.messagesLoading}
          onRefresh={data.onRefreshMessages}
          header={config.label || data.messagesHeader}
          metadata={data.messagesMetadata}
        />
      );

    case 'whatsapp_send':
      return (
        <WhatsAppSendSection
          contacts={data.contacts}
          contactsLoading={data.contactsLoading}
          onSend={data.onSendWhatsApp}
          isSending={data.isSending}
        />
      );

    case 'chatter_post':
      // Schema is truth - always render, component handles disabled state
      return (
        <ChatterPostSection
          onPost={data.onPostToChatter}
          isPosting={data.isPostingChatter}
        />
      );

    case 'messenger_tabs':
      return (
        <MessengerTabsSection
          channels={config.channels || ['chatter']}
          collapsedByDefault={config.collapsed_by_default ?? true}
          contacts={data.contacts}
          contactsLoading={data.contactsLoading}
          onSendWhatsApp={data.onSendWhatsApp}
          onSendWeChat={data.onSendWeChat}
          onPostToChatter={data.onPostToChatter}
          isSendingWhatsApp={data.isSendingWhatsApp ?? data.isSending}
          isSendingWeChat={data.isSendingWeChat}
          isPostingChatter={data.isPostingChatter}
        />
      );

    default:
      console.warn(`[ChatSectionRenderer] Unknown section type: ${config.type}`);
      return null;
  }
}
